"use client";

/**
 * usePWAInstall
 *
 * Shared hook for PWA install state, used by the sidebar nav item, profile
 * page button, and any other component that wants to trigger installation.
 *
 * Returns:
 *  - canInstall   — true when the native prompt is ready (Android/Chrome)
 *  - isIos        — true on iOS where manual "Add to Home Screen" is needed
 *  - isInstalled  — already running as an installed PWA
 *  - neverDismissed — user chose "Never show again"
 *  - install()    — triggers the native install prompt
 *  - dismiss(forever?) — suppress banner (forever=true → permanent)
 */

import { useEffect, useState, useCallback } from "react";

export const INSTALL_NEVER_KEY  = "bb-pwa-never";
export const INSTALL_REMIND_KEY = "bb-pwa-remind-at";
export const REMIND_MS          = 14 * 24 * 60 * 60 * 1000; // 14 days

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
  prompt(): Promise<void>;
}

function detectIos() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as unknown as Record<string, unknown>).MSStream;
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  } catch {
    return false;
  }
}

/** Safe localStorage wrapper — iOS private mode throws SecurityError on access */
function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore — private mode */ }
}

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall,   setCanInstall]   = useState(false);
  const [isIos,        setIsIos]        = useState(false);
  const [isInstalled,  setIsInstalled]  = useState(false);
  const [neverDismissed, setNeverDismissed] = useState(false);
  const [suppressed,   setSuppressed]   = useState(false);

  useEffect(() => {
    try {
      if (detectStandalone()) { setIsInstalled(true); return; }
      if (lsGet(INSTALL_NEVER_KEY)) { setNeverDismissed(true); return; }

      const remindAt = lsGet(INSTALL_REMIND_KEY);
      if (remindAt && Date.now() < parseInt(remindAt, 10)) { setSuppressed(true); return; }

      if (detectIos()) { setIsIos(true); return; }

      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setCanInstall(true);
      };
      const installedHandler = () => {
        setCanInstall(false);
        setIsInstalled(true);
      };
      window.addEventListener("beforeinstallprompt", handler);
      window.addEventListener("appinstalled", installedHandler);
      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        window.removeEventListener("appinstalled", installedHandler);
      };
    } catch {
      // Never let PWA detection crash the app
    }
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setCanInstall(false);
        setIsInstalled(true);
        lsSet(INSTALL_NEVER_KEY, "1");
      }
      return outcome === "accepted";
    } catch {
      return false;
    }
  }, [deferredPrompt]);

  const dismiss = useCallback((forever = false) => {
    if (forever) {
      lsSet(INSTALL_NEVER_KEY, "1");
      setNeverDismissed(true);
    } else {
      lsSet(INSTALL_REMIND_KEY, String(Date.now() + REMIND_MS));
      setSuppressed(true);
    }
    setCanInstall(false);
    setDeferredPrompt(null);
  }, []);

  return { canInstall, isIos, isInstalled, neverDismissed, suppressed, install, dismiss };
}
